import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { organisationsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { OrgLogo } from './OrgLogo';
import { Building2, ArrowRight } from 'lucide-react';
import { getOrgTypeLabel } from '../lib/utils';
import { getOrgType } from '../lib/orgTypes';

const formatYearMonth = (s) => {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{1,2})/);
  if (!m) return s;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m[2] - 1] || ''} ${m[1]}`.trim();
};
const formatRange = (start, end) => {
  const s = formatYearMonth(start);
  const e = end ? formatYearMonth(end) : 'Present';
  if (s && e) return `${s} → ${e}`;
  if (s) return `From ${s}`;
  return '';
};

/**
 * Surfaces other organisations linked to the current one.
 *
 * Two sections:
 * - Dependent: child orgs created from a placement (`parent_org_id` link).
 *   These are mentioned in the parent's SAR letter automatically.
 * - Overlapping: orgs whose date ranges overlap (e.g. the GP you attended
 *   while at this council). Informational only — not auto-included in SARs.
 */
export const LinkedOrganisations = ({ orgId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ dependent: [], overlapping: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await organisationsApi.getRelated(orgId);
        if (active) setData(res.data || { dependent: [], overlapping: [] });
      } catch {
        // soft-fail — card just doesn't render
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [orgId]);

  const totalLinks = (data.dependent?.length || 0) + (data.overlapping?.length || 0);
  if (loading || totalLinks === 0) return null;

  const renderRow = (o, kind) => (
    <Button
      key={o.id}
      variant="secondary"
      onClick={() => navigate(`/organisations/${o.id}`)}
      className="w-full justify-start gap-3 min-h-[56px] bg-ground hover:bg-inset"
      data-testid={`linked-${kind}-${o.id}`}
    >
      <OrgLogo
        orgId={o.id}
        website={o.website}
        FallbackIcon={getOrgType(o.type)?.icon || Building2}
        size={36}
        className="!rounded-lg flex-shrink-0"
      />
      <div className="text-left flex-1 min-w-0">
        <p className="font-medium truncate">{o.name}</p>
        <p className="text-sm text-ink-55 font-normal">
          {getOrgTypeLabel(o.type)}
          {(o.start_date || o.end_date) && (
            <> · {formatRange(o.start_date, o.end_date)}</>
          )}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-ink-55 flex-shrink-0" />
    </Button>
  );

  return (
    <Card className="mb-6" data-testid="linked-orgs-card">
      <CardHeader>
        <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Linked organisations</CardTitle>
        <CardDescription className="text-ink-55">Other records connected to this one.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.dependent?.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-ink">
              From a placement here
              <span className="ml-auto text-sm font-normal text-ink-55">
                Has its own request · also named in this organisation's letter
              </span>
            </div>
            <div className="flex flex-col gap-3" data-testid="linked-dependent-list">
              {data.dependent.map((o) => renderRow(o, 'dependent'))}
            </div>
          </div>
        )}

        {data.overlapping?.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-ink">
              Overlapping in time
              <span className="ml-auto text-sm font-normal text-ink-55">
                Tracked separately with its own request
              </span>
            </div>
            <div className="flex flex-col gap-3" data-testid="linked-overlapping-list">
              {data.overlapping.map((o) => renderRow(o, 'overlapping'))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkedOrganisations;
